import React, { useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import withStyles from 'react-jss';
import PropTypes from 'prop-types';
import { Table, Popconfirm, Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import { EditOutlined, DeleteOutlined, FileOutlined } from '@ant-design/icons';
import moment from 'moment';

import Breadcrumbs from '../Components/Breadcrumbs';
import {
  sorter,
  onFilter,
  filterDropdown,
  filterIcon,
  get,
  dateSorter,
  onDateFilter,
  dateFilterDropdown,
} from '../utils';
import Api from '../Api';
import { getWorkerIssues, clearWorkerIssues } from '../Actions';
import { errorNotification, successNotification } from '../Snackbar';
import styles from '../styles';
import constants from '../constants';

const WorkerIssueList = props => {
  const { classes } = props;
  const dispatch = useDispatch();

  const auth = useSelector(state => state.auth);
  const permissions = useMemo(() => get(auth, ['permissions'], []), [auth]);

  const workerIssues = useSelector(state => state.workerIssues);

  useEffect(() => {
    dispatch(getWorkerIssues());

    return () => {
      dispatch(clearWorkerIssues());
    };
  }, []);

  const onDelete = useCallback(async id => {
    try {
      await Api.delete(`/worker-jobs/issues/${id}`);
      successNotification('deleted');
      dispatch(getWorkerIssues());
    } catch (err) {
      errorNotification(err);
    }
  }, []);

  const onApprove = useCallback(async id => {
    try {
      await Api.put(`/worker-jobs/issues/${id}/approve`);
      successNotification('approved');
      dispatch(getWorkerIssues());
    } catch (err) {
      errorNotification(err);
    }
  }, []);

  const columns = useMemo(() => {
    const _columns = [
      {
        title: 'issue Time',
        dataIndex: 'issueTime',
        sorter: dateSorter('issueTime'),
        onFilter: onDateFilter('issueTime'),
        filterDropdown: dateFilterDropdown,
        filterIcon,
        render: issueTime =>
          issueTime ? moment(issueTime).format('DD MMM YYYY') : null,
      },
      {
        title: 'Styles',
        dataIndex: 'styles',
        sorter: sorter('styles'),
        onFilter: onFilter('styles'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
        sorter: sorter('remark'),
        onFilter: onFilter('remark'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Created By',
        dataIndex: 'createdBy',
        sorter: sorter('createdBy'),
        onFilter: onFilter('createdBy'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Approved By',
        dataIndex: 'approvedBy',
        sorter: sorter('approvedBy'),
        onFilter: onFilter('approvedBy'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'View',
        render: record => (
          <Tooltip placement="bottom" title="View">
            <Link to={`/workers/issues/${record.id}`}>
              <FileOutlined className={classes.tableIcon} />
            </Link>
          </Tooltip>
        ),
      },
    ];

    if (permissions.includes(constants.permissions.updateWorkerIssue)) {
      _columns.push({
        title: 'Update',
        render: record => (
          <Tooltip placement="bottom" title="Update">
            <Link to={`/workers/update/${record.id}`}>
              <EditOutlined className={classes.tableIcon} />
            </Link>
          </Tooltip>
        ),
      });
    }

    if (permissions.includes(constants.permissions.approveWorkerIssue)) {
      _columns.push({
        title: 'Approve',
        render: record =>
          !record.isApproved ? (
            <Tooltip placement="bottom" title="Approve">
              <Popconfirm
                title="Are you sure you want to approve?"
                onConfirm={() => onApprove(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <EditOutlined className={classes.tableIcon} />
              </Popconfirm>
            </Tooltip>
          ) : null,
      });
    }

    if (permissions.includes(constants.permissions.deleteWorkerIssue)) {
      _columns.push({
        title: 'Delete',
        render: record =>
          !record.isDeleted ? (
            <Tooltip placement="bottom" title="Delete">
              <Popconfirm
                title="Are you sure you want to delete?"
                onConfirm={() => onDelete(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <DeleteOutlined className={classes.tableIcon} />
              </Popconfirm>
            </Tooltip>
          ) : null,
      });
    }

    return _columns;
  }, []);

  return (
    <>
      <Breadcrumbs
        moduleName="Worker Style Issues"
        moduleLink="/workers/issues"
        subModuleName="List"
      />
      <div className={classes.tableStyle}>
        <Table
          bordered
          size="small"
          columns={columns}
          dataSource={workerIssues}
          rowKey={useMemo(() => record => record.id, [])}
          pagination={useMemo(
            () => ({
              defaultPageSize: 20,
              position: ['bottomLeft'],
              showTotal: total => `Total ${total} Records`,
            }),
            []
          )}
          scroll={useMemo(() => ({ x: 1200, y: window.innerHeight - 300 }), [])}
        />
      </div>
    </>
  );
};

WorkerIssueList.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(WorkerIssueList);
